import { CornerRightDown } from "lucide-react";
import Link from "next/link";

import DomPurify from "isomorphic-dompurify";
import Image from "components/Image";
import MaxWidthWrapper from "components/Tiles/commonComponents/MaxWidthWrapper";
import DualButtonCTA, { ButtonVariant } from "components/ui/DualButtonCTA";
import { FadeIn } from "components/ui/FadeIn";
import { Button, ButtonVariantTypes } from "components/ui/button";
import LottieAnimation from "./lottie";
interface ButtonProps {
  buttonText: string;
  buttonUrl: string;
  buttonVariant: ButtonVariant;
}

interface CallOutProps {
  title?: string;
  textColor?: string;
  bgColor?: string;
  button1?: ButtonProps;
  idAnchor?: string;
  showLottie?: boolean;
  button2?: ButtonProps;
  isTop?: boolean;
  src?: string;
  lottieData?: any;
  boxBgColor?: string;
  miniTitle?: string;
  miniTitleColor?: string;
  subHeader?: string;
  subTitle?: string;
  className?: string;
  titleTag?: React.ElementType;
  showLearn?: boolean;
}

// Tailwind classes for dynamic content: py-14 py-16 py-20 py-24 py-28 py-32 py-36 py-40 py-44 py-48

const CallOut = ({
  title,
  textColor,
  boxBgColor,
  isTop = false,
  showLottie = true,
  lottieData,
  src,
  bgColor,
  idAnchor,
  miniTitle,
  miniTitleColor,
  button1,
  subHeader,
  button2,
  subTitle,
  titleTag = "h2",
  showLearn = false,
  className
}: CallOutProps) => {
  const TitleTag = titleTag;

  return (
    <section
      id={idAnchor || "CallOut-103"}
      style={{ backgroundColor: bgColor, marginTop: isTop ? "-81px" : "0" }} // Conditional marginTop based on isTop
      className={`pb-24 pt-32 ${className}`}
    >
      <MaxWidthWrapper>
        <div style={{ backgroundColor: boxBgColor }} className={`rounded-xl px-5 py-14 ${className}`}>
          <div className={`grid grid-cols-1  ${src ? "gap-x-12 lg:grid-cols-10" : ""} ${isTop ? "pt-[81px]" : ""}`}>
            <div
              className={`col-span-1 ${src ? "pb-8 pt-16 lg:col-span-5" : "lg:col-span-10"} ${
                !src ? "mx-auto max-w-4xl px-4 text-left lg:px-0 lg:text-center" : ""
              }`}
            >
              <FadeIn>
                {miniTitle && (
                  <p
                    className="mb-4 text-center text-[18px] font-semibold text-white"
                    style={{ color: miniTitleColor }}
                  >
                    {miniTitle}
                  </p>
                )}
                <TitleTag
                  className="mb-6 text-center font-heading text-[42px] leading-[48px] [text-wrap:balance] lg:text-[52px] lg:leading-[64px]"
                  style={{ color: textColor }}
                  dangerouslySetInnerHTML={{ __html: DomPurify.sanitize(title || "") }} // Render the HTML string
                />

                {subHeader && (
                  <h2 className="mb-8 text-center text-lg [text-wrap:balance] lg:text-2xl" style={{ color: textColor }}>
                    {subHeader}
                  </h2>
                )}
                {subTitle && (
                  <p className="mb-8 text-center text-md [text-wrap:balance] lg:text-lg" style={{ color: textColor }}>
                    {subTitle}
                  </p>
                )}

                {(button1 || button2) && <DualButtonCTA button1={button1} button2={button2} isLeftAligned={false} />}

                {showLearn && (
                  <div className="mt-8 text-center">
                    <Button
                      variant={ButtonVariantTypes.LINK_ICON_ARROW}
                      className="ml-6 items-center justify-center"
                      icon={
                        <CornerRightDown size={20} className="corner-right-down-icon ml-2 mt-[10px]" strokeWidth={2} />
                      }
                    >
                      <Link href="#atom-layout-69"> Or Learn More</Link>
                    </Button>
                  </div>
                )}

                {showLottie && lottieData && (
                  <div className="mt-8 text-center">
                    <div className="mx-auto h-[330px] w-[400px]">
                      <LottieAnimation animationData={lottieData} className="h-full w-full" />
                    </div>
                  </div>
                )}
              </FadeIn>
            </div>
            {src && (
              <div className="col-span-1  lg:col-span-5">
                <div className="relative flex min-h-full items-center justify-center">
                  <Image
                    alt="renovate fearlessly with SoldAve"
                    src={src}
                    quality={80}
                    className="rounded-xl"
                    fill
                    priority
                    style={{
                      objectFit: "cover"
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </MaxWidthWrapper>
    </section>
  );
};

export default CallOut;
